/**
 * React hook for managing RMC order state in the two-panel layout
 */

import {FieldsForm} from '@hconnect/tuc-processes/fields/types'
import {useOrderProcessFormContext} from '@hconnect/tuc-processes/form/useOrderProcessFormContext'
import _ from 'lodash'
import {useCallback, useMemo, useState} from 'react'

import {usePersistRmcContact} from '../../../../common/react-query/hooks/mutations/usePersistRmcContact'
import {usePersistRmcOrder} from '../../../../common/react-query/hooks/mutations/usePersistRmcOrder'
import {useFeaturesState} from '../../../../Organisms/Features'
import {
  ORDER_CREATE_API_PATH,
  V2_ORDER_REQUESTS_API_PATH
} from '../../../../Organisms/OrderIntake/OrderIntake.action'
import {useJobSite} from '../contexts/JobSiteContext'
import {placeRmcOrders} from '../services/rmcOrderPlacementService'
import {
  RmcBulkOrderEditState,
  RmcOrder,
  RmcOrderPlacementResult,
  RmcOrderStatus,
  RmcOrderUpdateOptions
} from '../types/order'

/**
 * Hook for managing RMC orders in the two-panel layout
 */
export const useRmcBulkOrderEdit = () => {
  const {jobSite} = useJobSite()
  const {mutate: persistRmcOrder} = usePersistRmcOrder()
  const {mutate: persistRmcContact} = usePersistRmcContact()
  const {getFeature} = useFeaturesState()
  const enableOrderCreateApi = !!getFeature('EnableOrderCreateApi')
  const orderCreateApiPath = enableOrderCreateApi
    ? ORDER_CREATE_API_PATH
    : V2_ORDER_REQUESTS_API_PATH

  const {reset, trigger} = useOrderProcessFormContext()

  const [state, setState] = useState<RmcBulkOrderEditState>({
    orders: [],
    selectedOrderId: null,
    isPlacingOrders: false,
    placementError: null,
    lastPlacedOrderIds: [],
    errors: {},
    hasDateResets: false,
    placementResult: null
  })

  const buildOrder = useCallback(
    (
      formData: FieldsForm,
      status: RmcOrderStatus,
      options?: {requiresDispatchApproval?: boolean}
    ): RmcOrder => ({
      id: _.uniqueId(),
      createdAt: new Date(),
      updatedAt: new Date(),
      status,
      formData: _.cloneDeep(formData),
      requiresDispatchApproval: options?.requiresDispatchApproval
    }),
    []
  )

  /**
   * Creates a new order from form data
   */
  const createOrder = useCallback(
    (formData: FieldsForm, options?: {requiresDispatchApproval?: boolean}) => {
      // Create a new order using truck configuration
      // Uses the more accurate truck capacity from the unified truck configuration
      const newOrder = buildOrder(formData, RmcOrderStatus.READY, options)

      setState((prevState) => ({
        ...prevState,
        orders: [...prevState.orders, newOrder],
        selectedOrderId: newOrder.id // Auto-select the new order
      }))

      // Sync form context with the new order's data - same as we did in selectOrder because here we Auto-select the new order
      reset(newOrder.formData)
      void trigger()

      return newOrder.id
    },
    [buildOrder, reset, trigger]
  )

  const initializeOrders = useCallback(
    async (formDataArray: FieldsForm[]) => {
      const initializedOrders: RmcOrder[] = []

      for (const formData of formDataArray) {
        reset(formData)
        const isValid = await trigger()
        initializedOrders.push(
          buildOrder(formData, isValid ? RmcOrderStatus.READY : RmcOrderStatus.INVALID)
        )
      }

      setState((prevState) => ({
        ...prevState,
        orders: [...prevState.orders, ...initializedOrders],
        selectedOrderId: initializedOrders.at(-1)?.id ?? prevState.selectedOrderId
      }))
    },
    [buildOrder, reset, trigger]
  )

  /**
   * Updates an existing order with new form data
   */
  const updateOrder = useCallback((formData: FieldsForm, options?: RmcOrderUpdateOptions) => {
    setState((prevState) => {
      if (!prevState.selectedOrderId) {
        return prevState
      }

      return {
        ...prevState,
        orders: prevState.orders.map((order) => {
          if (order.id !== prevState.selectedOrderId) {
            return order
          }
          return {
            ...order,
            formData: _.cloneDeep(formData),
            status:
              options?.isValid === undefined
                ? order.status
                : options.isValid
                  ? RmcOrderStatus.READY
                  : RmcOrderStatus.INVALID,
            requiresDispatchApproval: options?.requiresDispatchApproval
          }
        })
      }
    })
  }, [])

  const updateRequiresDispatchApproval = useCallback(
    (orderId: string, requiresDispatchApproval: boolean) => {
      setState((prevState) => ({
        ...prevState,
        orders: prevState.orders.map((order) =>
          order.id === orderId ? {...order, requiresDispatchApproval} : order
        )
      }))
    },
    []
  )

  /**
   * Deletes an order
   * Prevents deletion if it's the last remaining order (must have at least one order)
   */
  const deleteOrder = useCallback((orderId: string) => {
    setState((prevState) => {
      // Prevent deletion if this is the last remaining order
      if (prevState.orders.length <= 1) {
        return prevState
      }

      return {
        ...prevState,
        orders: prevState.orders.filter((order) => order.id !== orderId),
        selectedOrderId: prevState.selectedOrderId === orderId ? null : prevState.selectedOrderId
      }
    })
  }, [])

  /**
   * Copies an existing order (for "Add Item" functionality)
   */
  const copyOrder = useCallback(
    (sourceOrderId: string) => {
      const sourceOrder = state.orders.find((order) => order.id === sourceOrderId)
      if (!sourceOrder) {
        return
      }
      createOrder(sourceOrder.formData, {
        requiresDispatchApproval: sourceOrder.requiresDispatchApproval
      })
    },
    [createOrder, state.orders]
  )

  /**
   * Copies the most recent order (convenience method for "Add Item" button)
   */
  const copyLastOrder = useCallback(() => {
    const lastOrder = state.orders[state.orders.length - 1]
    if (lastOrder) {
      copyOrder(lastOrder.id)
    } else {
      console.warn('No orders available to copy')
    }
  }, [state.orders, copyOrder])

  /**
   * Selects an order for viewing/editing
   */
  const selectOrder = useCallback(
    (orderId: string | null) => {
      setState((prevState) => ({
        ...prevState,
        selectedOrderId: orderId
      }))

      const order = state.orders.find((order) => order.id === orderId)
      if (!order) {
        return
      }

      reset(order.formData)
      void trigger()
    },
    [reset, state.orders, trigger]
  )

  /**
   * Resets all order data - used when changing jobsite
   */
  const resetOrders = useCallback(() => {
    setState({
      orders: [],
      selectedOrderId: null,
      isPlacingOrders: false,
      placementError: null,
      lastPlacedOrderIds: [],
      errors: {},
      hasDateResets: false,
      placementResult: null
    })
  }, [])

  /**
   * Sets the hasDateResets flag - used when persisted order dates are reset
   */
  const setHasDateResets = useCallback((value: boolean) => {
    setState((prevState) => ({
      ...prevState,
      hasDateResets: value
    }))
  }, [])

  /**
   * Places all ready orders
   */
  const placeOrders = useCallback(async (): Promise<RmcOrderPlacementResult> => {
    const readyOrders = state.orders.filter((order) => order.status === RmcOrderStatus.READY)

    if (readyOrders.length === 0) {
      const result: RmcOrderPlacementResult = {
        success: false,
        placedOrderIds: [],
        failedOrderIds: [],
        errors: {general: 'No ready orders to place'},
        requestIds: {}
      }
      setState((prevState) => ({
        ...prevState,
        errors: {general: 'No ready orders to place'}
      }))
      return result
    }

    setState((prevState) => ({
      ...prevState,
      isPlacingOrders: true,
      placementError: null,
      errors: {}
    }))

    try {
      // Update order statuses to PLACING
      setState((prevState) => ({
        ...prevState,
        orders: prevState.orders.map((order) =>
          readyOrders.some((readyOrder) => readyOrder.id === order.id)
            ? {...order, status: RmcOrderStatus.PLACING}
            : order
        )
      }))

      // Call the real API service with jobSite and pump settings
      if (!jobSite) {
        throw new Error('Job site is required for placing orders')
      }

      const result = await placeRmcOrders(
        readyOrders,
        jobSite,
        // Persistence callback - enrich payloads with material descriptions before storing
        (successfulPayloads) => {
          // Enrich payloads with orderedMaterialDescription from order data
          const enrichedPayloads = successfulPayloads.map((payload, index) => {
            const correspondingOrder = readyOrders[index]
            const materialDescription = correspondingOrder?.formData.material?.materialDescription
            const customerMaterialDescription =
              correspondingOrder?.formData.material?.customerMaterialDescription
            const materialId = correspondingOrder?.formData.material?.materialId

            return {
              ...payload,
              // Add materialDescription and customerMaterialDescription for better display in previous orders
              // but this is part of formData, can we just save it?
              materialId: materialId || 'MaterialId',
              materialDescription: materialDescription || payload.materialNumber || 'Material',
              customerMaterialDescription: customerMaterialDescription || undefined
            }
          })

          persistRmcOrder({
            siteNumber: jobSite.siteNumber,
            rmcOrderPayloads: enrichedPayloads
          })

          // Persist contact data from the first order in original RMC format
          const firstOrderFormData = readyOrders[0]?.formData
          if (firstOrderFormData) {
            persistRmcContact({
              siteNumber: jobSite.siteNumber,
              rmcContact: {
                orderPlacer: firstOrderFormData.orderPlacer,
                siteContact: firstOrderFormData.siteContact
              }
            })
          }
        },
        orderCreateApiPath
      )

      // Always update individual order statuses based on the result arrays
      // regardless of overall success flag (handles partial success/failure correctly)
      setState((prevState) => ({
        ...prevState,
        orders: prevState.orders.map((order) =>
          result.placedOrderIds.includes(order.id)
            ? {...order, status: RmcOrderStatus.PLACED}
            : result.failedOrderIds.includes(order.id)
              ? {...order, status: RmcOrderStatus.FAILED}
              : order
        ),
        isPlacingOrders: false,
        lastPlacedOrderIds: result.placedOrderIds,
        errors: result.errors,
        placementResult: result
      }))

      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to place orders'

      // Update order statuses to FAILED
      const errorResult = {
        success: false,
        placedOrderIds: [],
        failedOrderIds: readyOrders.map((order) => order.id),
        errors: {general: errorMessage},
        requestIds: {}
      }

      setState((prevState) => ({
        ...prevState,
        orders: prevState.orders.map((order) =>
          readyOrders.some((readyOrder) => readyOrder.id === order.id)
            ? {...order, status: RmcOrderStatus.FAILED}
            : order
        ),
        isPlacingOrders: false,
        placementError: errorMessage,
        errors: {general: errorMessage},
        placementResult: errorResult
      }))

      return errorResult
    }
  }, [state.orders, jobSite, persistRmcOrder, persistRmcContact])

  /**
   * Clears the placement error
   */
  const clearPlacementError = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      placementError: null,
      errors: {}
    }))
  }, [])

  /**
   * Gets the currently selected order
   */
  const selectedOrder = useMemo(() => {
    if (!state.selectedOrderId) return null
    return state.orders.find((order) => order.id === state.selectedOrderId) || null
  }, [state.selectedOrderId, state.orders])

  const canPlaceOrders = useMemo(
    () =>
      !state.isPlacingOrders &&
      state.orders.length > 0 &&
      state.orders.every((order) => order.status === RmcOrderStatus.READY),
    [state.isPlacingOrders, state.orders]
  )

  return {
    // State
    orders: state.orders,
    selectedOrderId: state.selectedOrderId,
    selectedOrder,
    isPlacingOrders: state.isPlacingOrders,
    placementError: state.placementError,
    lastPlacedOrderIds: state.lastPlacedOrderIds,
    errors: state.errors,
    hasDateResets: state.hasDateResets,
    placementResult: state.placementResult,

    // Actions
    createOrder,
    initializeOrders,
    updateOrder,
    updateRequiresDispatchApproval,
    deleteOrder,
    copyOrder,
    copyLastOrder,
    selectOrder,
    resetOrders,
    setHasDateResets,
    placeOrders,
    clearPlacementError,

    // Computed values
    hasReadyOrders: state.orders.some((order) => order.status === RmcOrderStatus.READY),
    hasOrders: state.orders.length > 0,
    hasSelectedOrder: !!selectedOrder,
    canDeleteOrders: state.orders.length > 1, // Can only delete if more than one order exists
    canPlaceOrders
  }
}
